import type { Mode } from "../../types/story";

/**
 * Typed telemetry events. Every event is timestamped and tied to
 * family / child / storyId / mode so analysis can group sessions.
 *
 * Privacy: `child` is a first name only. No other PII is captured.
 */

export type EventType =
  | "sessionStart"
  | "sceneViewed"
  | "audioCompleted"
  | "choiceMade"
  | "questionAnswered"
  | "questionSkipped"
  | "storyCompleted"
  | "replay";

/** Fields attached to every event. */
export interface EventBase {
  /** Client-generated unique id (for dedupe on export). */
  eventId: string;
  type: EventType;
  /** Epoch milliseconds. */
  ts: number;
  /** ISO-8601 for human-readable exports. */
  tsIso: string;
  family: string;
  child: string;
  storyId: string;
  mode: Mode;
  /** Groups all events from one playthrough/tab load. */
  sessionId: string;
}

export interface SessionStartEvent extends EventBase {
  type: "sessionStart";
  device: DeviceInfo;
}

export interface SceneViewedEvent extends EventBase {
  type: "sceneViewed";
  sceneId: string;
  /** How long the scene was on screen, ms. */
  dwellMs: number;
}

export interface AudioCompletedEvent extends EventBase {
  type: "audioCompleted";
  sceneId: string;
}

export interface ChoiceMadeEvent extends EventBase {
  type: "choiceMade";
  sceneId: string;
  optionIndex: number;
}

export interface QuestionAnsweredEvent extends EventBase {
  type: "questionAnswered";
  qId: string;
  optionIndex: number;
  correct: boolean;
}

export interface QuestionSkippedEvent extends EventBase {
  type: "questionSkipped";
  qId: string;
}

export interface StoryCompletedEvent extends EventBase {
  type: "storyCompleted";
  /** Ordered scene ids actually traversed. */
  path: string[];
  totalMs: number;
}

export interface ReplayEvent extends EventBase {
  type: "replay";
}

export type TelemetryEvent =
  | SessionStartEvent
  | SceneViewedEvent
  | AudioCompletedEvent
  | ChoiceMadeEvent
  | QuestionAnsweredEvent
  | QuestionSkippedEvent
  | StoryCompletedEvent
  | ReplayEvent;

/** Minimal, non-identifying device/OS context for sessionStart. */
export interface DeviceInfo {
  userAgent: string;
  platform: string;
  language: string;
  screenW: number;
  screenH: number;
  dpr: number;
  /** Coarse OS guess for grouping iOS Safari vs Android Chrome. */
  os: string;
}

export function collectDeviceInfo(): DeviceInfo {
  const ua = navigator.userAgent;
  let os = "other";
  if (/iPad|iPhone|iPod/.test(ua)) os = "ios";
  else if (/Android/.test(ua)) os = "android";
  else if (/Mac OS X/.test(ua)) os = "macos";
  else if (/Windows/.test(ua)) os = "windows";
  else if (/Linux/.test(ua)) os = "linux";

  return {
    userAgent: ua,
    platform: navigator.platform ?? "",
    language: navigator.language ?? "",
    screenW: window.screen?.width ?? 0,
    screenH: window.screen?.height ?? 0,
    dpr: window.devicePixelRatio ?? 1,
    os,
  };
}

export function newId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
